import type { BtlApplication } from '@api/btlApplicationForm/types';
import { ApplicationStatus } from '@api/btlApplicationForm/types';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import { App } from 'antd';
import { standardFormValidationProps } from '@common/formValidation';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from '@components/CaseSidebar/navigation/ooCaseSidebarNavigation';
import type { OoCaseSidebarContext } from '@components/CaseSidebar/buildOoCaseSidebar';
import { isOoCaseEditableStatus } from '@components/CaseSidebar/config/ooStatus';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { invalidateBtlApplicationData } from '@pages/BtlApplicationFormPage/btl-utils';
import { makeFormReadonly } from '@pages/BtlApplicationFormPage/makeFormReadonly';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mapHouseholdToOoExpensesFormValues } from './mapHouseholdToOoExpensesFormValues';
import { mapOoExpensesFormValuesToHousehold } from './mapOoExpensesFormValuesToHousehold';
import { ooExpensesFormSchema } from './ooExpensesFormSchema';

interface UseOoCaseExpensesFormParams {
  caseId: string;
  caseData?: BtlApplication;
  isCaseLoading: boolean;
  sidebarContext: OoCaseSidebarContext;
}

export function useOoCaseExpensesForm({
  caseId,
  caseData,
  isCaseLoading,
  sidebarContext,
}: UseOoCaseExpensesFormParams) {
  const { formatMessage } = useIntl();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.EXPENSES;

  const isFormEditable = isOoCaseEditableStatus(caseData?.status, sidebarContext.canEditDipAcceptedSections);
  const isDipAccepted =
    caseData?.status === ApplicationStatus.DIP_ACCEPTED && sidebarContext.canEditDipAcceptedSections;
  const nextSection = getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? '';

  if (isFormEditable && !formValidationContext) {
    throw new Error('OO expenses form must be used within a FormValidationProvider when editable');
  }

  const setIsSubmitting = formValidationContext?.setIsSubmitting ?? (() => undefined);

  const { data: households, isLoading: isHouseholdsLoading } = useQuery(
    ['btl-application-households', caseId],
    () => btlApplicationFormApi.getHouseholds(caseId),
    {
      enabled: Boolean(caseId) && !isCaseLoading,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const household = households?.[0];
  const householdId = household?.id;

  if (isCaseLoading || !caseId || isHouseholdsLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    isFormEditable,
    isDipAccepted,
    isDipAcceptedOrLater: isDipAccepted,
    // Households are loaded before this point (the hook returns early while they are pending), so
    // this is settled by the time the schema is built.
    hasStoredOccupiers: (household?.occupiers?.length ?? 0) > 0,
    hasStoredDependants: (household?.dependants?.length ?? 0) > 0,
  };

  const formSchema = ooExpensesFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    footerProps: {
      previousSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous'),
      nextSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next'),
      previousLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous'),
      nextLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next'),
    },
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      omitNil: false,
      name: 'oo_expenses',
      ...standardFormValidationProps,
      request: async () => mapHouseholdToOoExpensesFormValues(household),
      onFinish: isFormEditable
        ? async (values: Record<string, unknown>) => {
            try {
              setIsSubmitting(true);

              const householdPayload = mapOoExpensesFormValuesToHousehold(values);

              // Property outgoings are owned by the Property tab (DIP Accepted). Keep any values
              // already stored on the household so an Expenses save cannot zero them out.
              if (household?.declaredExpenditure) {
                householdPayload.declaredExpenditure = {
                  ...householdPayload.declaredExpenditure,
                  councilTax: household.declaredExpenditure.councilTax ?? 0,
                  serviceChargeGroundRent: household.declaredExpenditure.serviceChargeGroundRent ?? 0,
                };
              }

              if (householdId) {
                await btlApplicationFormApi.editHousehold(caseId, householdId, householdPayload);
              } else {
                const createdHousehold = await btlApplicationFormApi.createHousehold(caseId, householdPayload);
                const individuals = caseData?.borrower.individuals ?? [];
                const borrowerId = caseData?.borrower.id;

                if (borrowerId && individuals.length > 0) {
                  await Promise.all(
                    individuals.map(individual =>
                      btlApplicationFormApi.linkIndividualToHousehold(caseId, borrowerId, individual.id, {
                        householdId: createdHousehold.id,
                      }),
                    ),
                  );
                }
              }

              await invalidateBtlApplicationData(queryClient, caseId);
              await queryClient.invalidateQueries({ queryKey: ['btl-application-households', caseId] });

              if (nextSection) {
                navigate({ search: `?section=${nextSection}` });
              }
            } catch (error: unknown) {
              console.error(error);
              message.error(error instanceof Error ? error.message : 'Failed to save expenses');
            } finally {
              setIsSubmitting(false);
            }
          }
        : undefined,
    },
  };
}
